import type { MetadataRegistryEntry } from '@compass/client/types/metadata_registry';
import { BarChart3, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { EmptyState } from '@/components/EmptyState';
import { useMetadataAdvancedFilters } from '@/components/metadata-filters/MetadataAdvancedFilters';
import { StaticMultiSelect } from '@/components/ui/static-trigger-multi-select';
import type { SupportedTimeZone } from '@/constants/date';
import { ANALYTICS_SAVED_VIEWS_CONFIG } from '@/features/savedViews/config';
import { SavedViewsMenu } from '@/features/savedViews/SavedViewsMenu';
import { useDebounce } from '@/hooks/useDebounce';
import { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import { useOrgAnalytics } from '@/pages/analytics/hooks/useOrgAnalytics';
import { formatSelectedProgramScope } from '@/pages/analytics/utils/formatProgramScope';
import { normalizeOrgSummary } from '@/pages/analytics/utils/normalizeChartRows';
import {
  DEFAULT_VISIBLE_ORG_CHARTS,
  isOrgChartKey,
  ORG_CHART_OPTIONS,
  type OrgChartKey,
  orgChartVisibilityStorageKey,
} from '@/pages/analytics/utils/orgChartVisibility';
import { naiveDateToUtc } from '@/utils/date';
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type { OrgAnalyticsPdfSectionId } from '../utils/orgPdfExportSections';
import { AnalyticsFilters } from './AnalyticsFilters';
import { AnalyticsMetricsView } from './AnalyticsMetricsView';
import { AnalyticsPdfReportHeader, formatAnalyticsReportDate } from './AnalyticsPdfReportHeader';
import { OrgPdfExportMenu } from './OrgPdfExportMenu';

interface ProgramOption {
  label: string;
  value: string;
}

interface OrgPerformanceTabProps {
  organizationId: string;
  organizationName: string;
  programOptions: ProgramOption[];
  selectedProgramIds: string[];
  onProgramFilterChange: (next: string[]) => void;
  selectedCallDirections: string[];
  onCallDirectionsChange: (next: string[]) => void;
  start: string;
  end: string;
  timeZone: SupportedTimeZone;
  onDateRangeChange: (range: { start: string; end: string }) => void;
  metadataDefinitions: MetadataRegistryEntry[];
  selectedMetadata: Record<string, string[]>;
  onMetadataFilterChange: (key: string, values: string[]) => void;
}

export const OrgPerformanceTab: React.FC<OrgPerformanceTabProps> = ({
  organizationId,
  organizationName,
  programOptions,
  selectedProgramIds,
  onProgramFilterChange,
  selectedCallDirections,
  onCallDirectionsChange,
  start,
  end,
  timeZone,
  onDateRangeChange,
  metadataDefinitions,
  selectedMetadata,
  onMetadataFilterChange,
}) => {
  const exportRef = useRef<HTMLDivElement>(null);

  // Per-org chart show/hide selection, persisted to localStorage. Mirrors the
  // QC eval-list "Columns" dropdown: hydrate once per org, then persist a
  // debounced copy so rapid toggles don't thrash storage.
  const chartStorageKey = useMemo(
    () => orgChartVisibilityStorageKey(organizationId),
    [organizationId],
  );
  const [visibleCharts, setVisibleCharts] = useState<OrgChartKey[]>(DEFAULT_VISIBLE_ORG_CHARTS);
  const hydratedChartsRef = useRef(false);
  const debouncedVisibleCharts = useDebounce(visibleCharts, 200);

  useEffect(() => {
    hydratedChartsRef.current = false;
    const stored = getLocalStorageItem<string[]>(chartStorageKey);
    if (stored === null) {
      setVisibleCharts(DEFAULT_VISIBLE_ORG_CHARTS);
    } else {
      // A stored empty array is a legitimate "everything hidden" choice; only a
      // missing entry falls back to defaults.
      setVisibleCharts(stored.filter(isOrgChartKey));
    }
    hydratedChartsRef.current = true;
  }, [chartStorageKey]);

  useEffect(() => {
    if (!organizationId || !hydratedChartsRef.current) return;
    setLocalStorageItem(chartStorageKey, debouncedVisibleCharts);
  }, [debouncedVisibleCharts, organizationId, chartStorageKey]);

  const reportProgramScope = useMemo(
    () => formatSelectedProgramScope(programOptions, selectedProgramIds),
    [programOptions, selectedProgramIds],
  );
  const reportDateRange = useMemo(
    () => `${formatAnalyticsReportDate(start)} - ${formatAnalyticsReportDate(end)}`,
    [end, start],
  );
  const query = useOrgAnalytics({
    organizationId,
    programIds: selectedProgramIds,
    callDirections: selectedCallDirections,
    start,
    end,
    timeZone,
    metadata: selectedMetadata,
  });

  const hasActiveMetadata = useMemo(
    () => Object.values(selectedMetadata).some(values => values.length > 0),
    [selectedMetadata],
  );
  const metadataCallStartTimeAfter = useMemo(
    () => (start ? naiveDateToUtc(start, timeZone, 'start') : undefined),
    [start, timeZone],
  );
  const metadataCallStartTimeBefore = useMemo(
    () => (end ? naiveDateToUtc(end, timeZone, 'end') : undefined),
    [end, timeZone],
  );
  const { toggle: metadataToggle, panel: metadataPanel } = useMetadataAdvancedFilters({
    definitions: metadataDefinitions,
    selectedValues: selectedMetadata,
    onChange: onMetadataFilterChange,
    organizationId,
    programIds: selectedProgramIds,
    callStartTimeAfter: metadataCallStartTimeAfter,
    callStartTimeBefore: metadataCallStartTimeBefore,
    defaultExpanded: hasActiveMetadata,
  });
  // The query is gated on start/end (see useOrgAnalytics), which are populated a
  // render after mount by the date-defaults effect. Treat that pre-date window as
  // loading so charts show skeletons instead of flashing empty states. See AQ-476.
  const loading = query.isLoading || !start || !end;
  const download = useDownloadChart();

  const drilldownScope = useMemo(
    () => ({
      dateFrom: start,
      dateTo: end,
      programIds: selectedProgramIds,
      callDirections: selectedCallDirections,
      metadata: selectedMetadata,
    }),
    [start, end, selectedProgramIds, selectedCallDirections, selectedMetadata],
  );
  const handleDownload = (sectionIds: OrgAnalyticsPdfSectionId[]): Promise<void> | undefined => {
    if (!query.data) return undefined;

    return download(
      {
        ...normalizeOrgSummary(query.data.summary),
        filenameStem: 'organization_performance',
      },
      'pdf',
      'Organization Performance',
      exportRef.current,
      { includedSectionIds: sectionIds },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <AnalyticsFilters
          programOptions={programOptions}
          selectedProgramIds={selectedProgramIds}
          onProgramFilterChange={onProgramFilterChange}
          selectedCallDirections={selectedCallDirections}
          onCallDirectionsChange={onCallDirectionsChange}
          start={start}
          end={end}
          timeZone={timeZone}
          onDateRangeChange={onDateRangeChange}
          trailingFilters={metadataToggle}
        />

        <div className="flex items-center gap-2">
          <StaticMultiSelect
            options={ORG_CHART_OPTIONS}
            selected={visibleCharts}
            onChange={values => setVisibleCharts(values.filter(isOrgChartKey))}
            placeholder="Customize"
            searchPlaceholder="Search charts..."
            emptyMessage="No charts found"
            clearAllLabel="Hide all"
          />
          <OrgPdfExportMenu onExport={handleDownload} disabled={!query.data} />
          <SavedViewsMenu config={ANALYTICS_SAVED_VIEWS_CONFIG} organizationId={organizationId} />
        </div>
      </div>

      {metadataPanel}

      {query.error ? (
        <EmptyState
          title="Couldn't load org analytics"
          description={
            query.error instanceof Error
              ? query.error.message
              : 'The analytics request failed. Please try again.'
          }
          icon={XCircle}
          action={{ label: 'Retry', onClick: () => query.refetch() }}
        />
      ) : (
        <div ref={exportRef} className="flex flex-col gap-4">
          <AnalyticsPdfReportHeader
            icon={BarChart3}
            title="Organization Performance"
            metaItems={[
              { label: 'Organization', value: organizationName || organizationId },
              { label: 'Date range', value: reportDateRange },
              { label: 'Programs', value: reportProgramScope },
            ]}
          />
          <AnalyticsMetricsView
            data={query.data}
            loading={loading}
            download={download}
            organizationId={organizationId}
            visibleCharts={visibleCharts}
            drilldownScope={drilldownScope}
          />
        </div>
      )}
    </div>
  );
};
